/**
 * Card 097 — single source of truth for "do we know which tenant this
 * dashboard call is for?"
 *
 * The previous chrome scattered three different checks across Shell,
 * Home, ProjectPicker and HeaderBar:
 *
 *   const activeTenant = currentTenantId();   // truthy fallback to DEMO_TENANT_UUID!
 *   if (!activeTenant) return;                // never fires
 *   api('/v1/admin/projects')                 // 404 because demo tenant doesn't exist
 *
 * `currentTenantId()` falls back to `DEMO_TENANT_UUID` when localStorage
 * is empty (right after a fresh sign-in / cleared site data), so the
 * truthy gate was never a gate.  Every admin fetch fired with a bogus
 * tenant header and 404/503'd in a swarm before the operator had even
 * picked one.
 *
 * This hook returns `isResolved` ONLY when:
 *   1. `useMyTenants()` finished loading membership from /platform/tenants
 *   2. The localStorage tenant id matches one of the user's memberships
 *
 * Callers gate every `/v1/admin/*` fetch on `isResolved`.  Until then
 * the chrome shows a quiet skeleton instead of an error rain.
 */

import { useEffect, useMemo } from 'react';

import { currentTenantId, setActiveTenantId } from '../api.js';
import { useMyTenants } from '../components/TenantPicker.jsx';

export function useResolvedTenant() {
  const { tenants, isSuperAdmin, loading, reload } = useMyTenants();
  const storedTenantId = currentTenantId();

  // Map the stored id against the user's actual memberships.  null when
  // storage is empty, stale (revoked membership), or set to a default
  // UUID that doesn't exist on this backend (DEMO_TENANT_UUID).
  const activeTenant = useMemo(
    () => tenants.find((t) => t.tenant_id === storedTenantId) || null,
    [tenants, storedTenantId],
  );

  // Once tenants load, if the stored id doesn't match any membership AND
  // we have at least one tenant, snap to the first.  Keeps a fresh
  // sign-in / cleared-site-data flow from being stuck on the empty
  // state until the operator manually opens the picker.
  useEffect(() => {
    if (loading) return;
    if (activeTenant) return;
    if (tenants.length === 0) return;
    setActiveTenantId(tenants[0].tenant_id);
  }, [loading, activeTenant, tenants]);

  const tenantId = activeTenant?.tenant_id || null;
  return {
    tenantId,
    tenant: activeTenant,
    isResolved: !loading && tenantId != null,
    isLoading: loading,
    tenants,
    isSuperAdmin,
    reload,
  };
}
